import { useEffect, useMemo, useState } from "react";
import { useGetTalentList } from "../../../../net/CharacterFeatureManagement";
import { TableSort } from "../../../../common/components/layout/Table/Table";
import { useWindowDimensions } from "../../../../common/utilities/WindowDimensions";
import type { SortKey } from "../../../../common/utilities/sorters/TalentSorter";
import Cookies from "universal-cookie";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import Modal from "../../../../common/components/modals/Modal/Modal";
import Talent from "../../../../utils/game/mechanics/Talent";
import Table from "../../../../common/components/layout/Table/Table";
import TalentSorter from "../../../../common/utilities/sorters/TalentSorter";
import TextField from "../../../../common/components/display/TextField/TextField";
import TextInput from "../../../../common/components/input/TextInput/TextInput";

import "../style/CommonModalStyle_style.css";

type Props = {
    isVisible?: boolean;
    talentOptions?: Array<Talent>;
    /** Filters talent options based on external properties */
    optionFilter?: ((t: Talent) => boolean) | null;
    onTalentOptionsChanged?: (talents: Array<Talent>) => void;
    onClose?: () => void;
    onConfirm?: (talents: Array<Talent>) => void;
};



export default function LoadTalentModal({isVisible, talentOptions, optionFilter, onTalentOptionsChanged, onClose, onConfirm}: Props): JSX.Element {
    const [loadTalentList, isLoadingTalentList] = useGetTalentList();
    const [selectedTalentIDs, setSelectedTalentIDs] = useState<Array<number>>(
        []
    );
    const [talentList, setTalentList] = useState<Array<Talent>>(
        talentOptions ?? []
    );
    const [talentListSort, setTalentListSort] =
        useState<TableSort | null>(null);
    const [searchStr, setSearchStr] = useState<string>("");

    const talentSorter = useMemo(() => {
        return TalentSorter.createSorter(talentListSort?.key, talentListSort?.order);}, [talentListSort]);

    const cookies = new Cookies();

    const windowDimensions = useWindowDimensions();

    useEffect(() => {
        if (talentOptions === undefined) {
            loadTalentList({
                variables: { token: cookies.get("session_token") },
                onComplete: (result) => {
                    setTalentList(result);
                    onTalentOptionsChanged?.(result);
                },
                onError: (error) => {},
            });
        }
    }, []);

    const tableFilter = useMemo(() => {
        const searchFilter =
            searchStr.length > 0
                ? (talent: Talent) => {
                      if (optionFilter != null) {
                          if (!optionFilter(talent)) {
                              return false;
                          }
                      }
                      const match = new RegExp(searchStr, "i");
                      const result =
                          talent.title.match(match) ??
                          talent.subtype.match(match) ??
                          talent.proficiency.join().match(match) ??
                          talent.description.match(match);

                      return result !== null;
                  }
                : null;

        const filters = [optionFilter, searchFilter].flatMap(
            (f) => f ?? []
        );
        return filters.length > 0
            ? (talent: Talent) => filters.every((filter) => filter(talent))
            : null;
    }, [optionFilter, searchStr]);

    return (
        <Modal
            contentClassName="character-common-modal"
            header="Add Talent"
            isVisible={isVisible}
            width={windowDimensions.width >= 900 ? 750 : 400}
            footer="standard-submit-cancel"
            onClose={onClose}
            onConfirm={() => {
                onConfirm?.(
                    talentList.filter((talent) =>
                        selectedTalentIDs.includes(talent.id)
                    )
                );
            }}
        >
            <FlexRow>
                <FlexRow>
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >
                        Search
                    </TextField>
                    <TextInput
                        value={searchStr}
                        style={{
                            width: `${
                                windowDimensions.width >= 900
                                    ? "240px"
                                    : "120px"
                            }`,
                        }}
                        onChange={setSearchStr}
                    />
                </FlexRow>
                <FlexRow alignment="end">
                    <TextField
                        type="bold"
                        style={{ width: "auto", margin: "8px" }}
                    >{`${selectedTalentIDs.length} Selected`}</TextField>
                    <button
                        onClick={() => {
                            setSelectedTalentIDs([]);
                        }}
                    >
                        Clear
                    </button>
                </FlexRow>
            </FlexRow>
            <hr />
            <Table
                className="options-list"
                columns={[
                    {
                        columnKey: "title",
                        columnHeader: "Title",
                        sortable: true,
                        width: "25%",
                    },
                    {
                        columnKey: "subtype",
                        columnHeader: "Subtype",
                        sortable: true,
                        width: "20%",
                    },
                    {
                        columnKey: "proficiency",
                        columnHeader: "Proficiency",
                        sortable: true,
                        width: "15%",
                    },
                    {
                        columnKey: "description",
                        columnHeader: "Description",
                        sortable: true,
                        width: "40%",
                    },
                ]}
                rowData={talentList.sort(talentSorter)}
                cellParser={[
                    {
                        columnKey: "title",
                        callback: (talent) => {
                            return (
                                <TextField className="option-item">
                                    {talent.title}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "subtype",
                        callback: (talent) => {
                            return (
                                <TextField className="option-item">
                                    {talent.subtype}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "proficiency",
                        callback: (talent) => {
                            return (
                                <TextField className="option-item" numberOfLines={3}>
                                    {talent.proficiency.join(", ")}
                                </TextField>
                            );
                        },
                    },
                    {
                        columnKey: "description",
                        callback: (talent) => {
                            return (
                                <TextField
                                    className="option-item"
                                    numberOfLines={10}
                                >
                                    {talent.description}
                                </TextField>
                            );
                        },
                    },
                ]}
                idGetter={(talent) => talent.id.toString()}
                sort={talentListSort}
                sorter={talentSorter}
                onChangeSort={setTalentListSort}
                isRowSelected={(talent) =>
                    selectedTalentIDs.includes(talent.id)
                }
                onRowClick={(talent) => {
                    if (selectedTalentIDs.includes(talent.id)) {
                        setSelectedTalentIDs([
                            ...selectedTalentIDs.filter(
                                (entry) => entry !== talent.id
                            ),
                        ]);
                    } else {
                        setSelectedTalentIDs([...selectedTalentIDs, talent.id]);
                    }
                }}
                rowFilter={tableFilter}
                showHeader={true}
            />
        </Modal>
    );
}
